<template>
  <div :class="$options.name">
    <v-menu close-on-click close-on-content-click>
      <template v-slot:activator="{ on }">
        <v-btn
          color="warning"
          class="elevation-0"
          title="Импорт данных"
          dark
          v-on="on"
        >
          <v-icon>file_upload</v-icon>
        </v-btn>
      </template>
      <v-list>
        <v-subheader>Импорт данных</v-subheader>

        <v-list-item @click="onExcelImport">
          <v-list-item-title>Excel</v-list-item-title>
        </v-list-item>

        <v-list-item @click="onAIUSJSONImport">
          <v-list-item-title>JSON</v-list-item-title>
        </v-list-item>

        <v-list-item @click="onGeoJsonImport">
          <v-list-item-title>GeoJson</v-list-item-title>
        </v-list-item>

        <v-list-item @click="onCsvImport">
          <v-list-item-title>CSV</v-list-item-title>
        </v-list-item>

        <v-list-item @click="onKmlImport">
          <v-list-item-title>KML/KMZ</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-dialog v-model="dialog" :persistent="isLoading" max-width="800" scrollable>
      <v-card :loading="isLoading">
        <v-card-title>Импорт данных</v-card-title>

        <v-card-text>
          <v-container>
            <template>
              <h4>Выберите файл для импорта</h4>
              <v-file-input
                v-model="selectedFile"
                label="Выберите файл"
                :accept="acceptedFileTypes"
                @change="onFileSelected"
              />
            </template>

            <template v-if="secondStepIsAllowed && !isNotRequiredStep">
              <template v-if="byTwoCoords">
                <h4>Выберите поля, где находятся координаты</h4>
                <v-select
                  v-model="form.geoColumns.latitude"
                  :items="importData.columns"
                  label="Выберите поле с широтой"
                  @change="onGeoColumnChange($event, form.geoColumns.longitude)"
                />
                <v-select
                  v-model="form.geoColumns.longitude"
                  :items="importData.columns"
                  label="Выберите поле с долготой"
                  @change="onGeoColumnChange(form.geoColumns.latitude, $event)"
                />
              </template>
              <template v-else>
                <h4>Выберите поле, где находятся координаты</h4>
                <v-select
                  v-model="form.geoColumn"
                  :items="importData.columns"
                  label="Выберите поле"
                  @change="onGeoColumnChange"
                />
              </template>
              <v-checkbox
                v-if="isExcelType"
                v-model="byTwoCoords"
                label="Выбрать координаты по двум полям"
              />
            </template>

            <template v-if="fourthStepIsAllowed">
              <v-row>
                <v-btn text
                       small
                       color="primary"
                       @click="fillPropertiesFields"
                >
                  <u>Заполнить все поля</u>
                </v-btn>
              </v-row>
              <template>
                <h4>Сопоставте поля, данные из которых необходимо сохранить для объекта</h4>
                <div v-for="key in propertyKeys" :key="key.id">
                  <v-select
                    v-model="form.propertiesFields[key.id]"
                    :label="key.name"
                    :items="importData.columns"
                  />
                </div>
              </template>
              <template>
                <h4>Выберите поле, где содержится дата события</h4>
                <small>Необязательное поле</small>
                <v-select
                  v-model="form.happenedAtField"
                  :items="importData.columns"
                  label="Выберите поле"
                />
              </template>
              <template>
                <h4>Удалить все данные перед импортом</h4>
                <v-checkbox
                  v-model="form.removeGeometries"
                  label="Удалить все данные перед импортом"
                />
              </template>
              <template v-if="isExcelType">
                <h4>Выберите листы для импорта</h4>
                <small>Выберите листы для импорта</small>
                <v-radio-group v-model="form.allSheetsMode">
                  <v-radio
                    v-for="n in [false, true]"
                    :key="n"
                    :label="allSheetsModeLabel(n)"
                    :value="n"
                  />
                </v-radio-group>
              </template>
            </template>

            <template v-if="secondStepIsAllowed">
              <h4 class="mb-1">Краткие сведения об импорте:</h4>

              <div class="font-weight-regular">
                Общее количество элементов:
                <span class="font-weight-bold">
                  {{ importableSummary.total }}
                </span>
              </div>

              <div
                class="font-weight-regular"
                :class="{ 'data-import-dialog__text-success': isImportable }"
              >
                Элементов доступных к импорту:
                <span class="font-weight-bold">
                  {{ importableSummary.importable }}
                </span>
              </div>

              <div class="font-weight-regular">
                <div :class="{ 'data-import-dialog__text-danger': isUnImportable }">
                  Элементов не доступных к импорту:
                  <span class="font-weight-bold">
                    {{ importableSummary.unImportable }}
                  </span>
                </div>

                <ul
                  v-if="isUnImportable"
                  class="reset-ul"
                >
                  <li :class="{ 'data-import-dialog__text-danger': isNoCoordinates }">
                    Координаты не определены:
                    <span class="font-weight-bold">
                      {{ importableSummary.problemDetails.noCoordinates }}
                    </span>
                  </li>

                  <li :class="{'data-import-dialog__text-danger': isUnrecognizedCoordinates }">
                    Координаты не распознаны:
                    <span class="font-weight-bold">
                      {{ importableSummary.problemDetails.unrecognizedCoordinates }}
                    </span>
                  </li>

                  <li :class="{'data-import-dialog__text-danger': isOutOfUserRegion }">
                    Объекты вне региона пользователя:
                    <span class="font-weight-bold">
                      {{ importableSummary.problemDetails.outOfUserRegion }}
                    </span>
                  </li>
                </ul>
              </div>
            </template>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn color="blue darken-1" text @click="dialog = false">Отмена</v-btn>
          <v-btn
            color="success darken-1"
            text
            :disabled="!importIsAvailable"
            @click="onImportStart"
          >
            Выполнить импорт данных
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { requestImportableSummary, uploadImportFile } from '@/utils/requests/upload';
import importRequest from '@/utils/requests/import';

const EXCEL_IMPORT_TYPE = 'excel';
const AIUS_JSON_IMPORT_TYPE = 'aius-json';
const GEO_JSON_IMPORT_TYPE = 'geojson';
const CSV_IMPORT_TYPE = 'csv';
const KML_IMPORT_TYPE = 'kml';
const DEFAULT_IMPORT_TYPE = EXCEL_IMPORT_TYPE;

const createDefaultForm = () => ({
  geoColumn: null,
  objectId: null,
  propertiesFields: {},
  geoColumns: {
    latitude: null,
    longitude: null,
  },
  allSheetsMode: false,
  happenedAtField: null,
  removeGeometries: false,
});

const createDefaultImportableSummary = () => ({
  total: 0,
  importable: 0,
  unImportable: 0,
  problemDetails: {
    noCoordinates: 0,
    outOfUserRegion: 0,
    unrecognizedCoordinates: 0,
  },
});

export default {
  name: 'data-import-dialog',
  components: {},
  props: {
    objectType: {
      type: String,
      default: 'layer',
      validator: _ => ['layer', 'event'].includes(_),
    },
    objectId: String,
    label: {
      type: String,
      default: () => 'Импорт данных',
    },
    propertyKeys: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    dialog: false,
    type: DEFAULT_IMPORT_TYPE,
    isLoading: false,
    selectedFile: null,
    fileIsLoaded: false,
    importData: null,
    form: createDefaultForm(),
    layerStepType: null,
    newLayerData: null,
    byTwoCoords: false,
    importableSummary: createDefaultImportableSummary(),
  }),
  computed: {
    ...mapState('categories', {
      categoryList: 'list',
    }),

    isImportable() {
      return this.importableSummary.importable > 0;
    },

    isUnImportable() {
      return this.importableSummary.unImportable > 0;
    },

    isNoCoordinates() {
      return this.importableSummary.problemDetails.noCoordinates > 0;
    },

    isOutOfUserRegion() {
      return this.importableSummary.problemDetails.outOfUserRegion > 0;
    },

    isUnrecognizedCoordinates() {
      return this.importableSummary.problemDetails.unrecognizedCoordinates > 0;
    },

    acceptedFileTypes() {
      const fileTypes = {
        [AIUS_JSON_IMPORT_TYPE]: '.json',
        [GEO_JSON_IMPORT_TYPE]: '.json, .geojson',
        [EXCEL_IMPORT_TYPE]: '.xls, .xlsx',
        [CSV_IMPORT_TYPE]: '.csv',
        [KML_IMPORT_TYPE]: '.kml, .kmz',
      };
      return fileTypes[this.type];
    },

    allSheetsModeLabel() {
      return n => (n ? 'Все листы (структура листов должна быть одинаковой)' : 'Первый лист');
    },

    secondStepIsAllowed() {
      return !!this.selectedFile && !!this.fileIsLoaded && !!this.importData;
    },

    isGeoColumnFilled() {
      const { geoColumn, geoColumns } = this.form;
      return !!geoColumn || (!!geoColumns.latitude && !!geoColumns.longitude && this.byTwoCoords);
    },

    thirdStepIsAllowed() {
      return !!this.secondStepIsAllowed && this.isGeoColumnFilled;
    },

    thirdStepTitle() {
      if (this.layerStepType === 'new') return 'Новый слой';
      if (this.layerStepType === 'existing') return 'Существующий слой';

      return 'Слой';
    },

    fourthStepIsAllowed() {
      return !!this.thirdStepIsAllowed && !!this.form.objectId && !!this.layerStepType;
    },

    importIsAvailable() {
      return !!this.fileIsLoaded
        && this.fourthStepIsAllowed
        && this.importableSummary.importable > 0;
    },

    isNotRequiredStep() {
      return [GEO_JSON_IMPORT_TYPE, KML_IMPORT_TYPE].includes(this.type);
    },

    isExcelType() {
      return this.type === EXCEL_IMPORT_TYPE;
    },

    isCsvType() {
      return this.type === CSV_IMPORT_TYPE;
    },
  },
  watch: {
    dialog: 'cleanData',
  },
  async created() {
    this.fillPropertiesFields();
  },
  methods: {
    ...mapActions('layer-objects', {
      getLayers: 'getDataList',
      createLayer: 'create',
    }),
    ...mapActions(['showNotify']),

    async onGeoColumnChange(lat, lon) {
      if (this.byTwoCoords && (!lat || !lon)) return;

      this.isLoading = true;

      this.importableSummary = await requestImportableSummary(
        this.importData.id,
        this.byTwoCoords,
        [lat, lon],
      );

      this.isLoading = false;
    },

    onAIUSJSONImport() {
      this.type = AIUS_JSON_IMPORT_TYPE;
      this.dialog = true;
    },

    onExcelImport() {
      this.type = EXCEL_IMPORT_TYPE;
      this.dialog = true;
    },

    onGeoJsonImport() {
      this.type = GEO_JSON_IMPORT_TYPE;
      this.dialog = true;
    },

    onCsvImport() {
      this.type = CSV_IMPORT_TYPE;
      this.dialog = true;
    },

    onKmlImport() {
      this.type = KML_IMPORT_TYPE;
      this.dialog = true;
    },

    async onFileSelected(file) {
      if (!file) {
        this.fileIsLoaded = false;
        return;
      }

      try {
        this.isLoading = true;
        const response = await uploadImportFile(
          file,
          this.type,
          this.objectId,
          this.objectType,
        );

        this.importableSummary = response.importableSummary;
        this.importData = response;

        if (!this.importData) {
          this.isLoading = false;
          return;
        }

        if ([GEO_JSON_IMPORT_TYPE, KML_IMPORT_TYPE].includes(this.type)) {
          // В формате geojson, координаты передаются отдельно в ключе geometry
          // т.к. во втором шаге обязательно выбрать geoColumn, переключил его в 'pass'
          this.form.geoColumn = 'pass';
        } else {
          this.form.geoColumn = this.importData.geoColumn.value;
        }

        // todo edit this shit
        if (this.objectId) {
          this.layerStepType = 'existing';
          this.form.objectId = this.objectId;
        }

        this.fileIsLoaded = true;
      } finally {
        this.isLoading = false;
      }
    },

    async onNewLayerCreate(data) {
      this.isLoading = true;
      const newLayer = await this.createLayer(data);

      this.layerList = await this.getLayers();
      this.form.objectId = newLayer.id;
      this.layerStepType = 'existing';
      this.isLoading = false;
    },

    async onImportStart() {
      this.isLoading = true;

      const data = {
        importDataId: this.importData.id,
        objectId: this.form.objectId,
        propertiesFields: this.form.propertiesFields,
        allSheetsMode: this.form.allSheetsMode,
        happenedAtField: this.form.happenedAtField,
        removeGeometries: this.form.removeGeometries,
      };

      if (this.byTwoCoords) {
        data.pointLocations = this.form.geoColumns;
      } else {
        data.pointLocation = this.form.geoColumn;
      }

      try {
        const response = await importRequest(data);
        const { importedItemsCount, isQueued } = response.data;
        if (isQueued) {
          this.showNotify({ message: 'Импорт файла запущен. Проверить его статус можно на вкладке "Импорт"' });
        } else {
          this.showNotify({ message: `Успешно импортировано ${importedItemsCount} элементов` });
        }
        this.$emit('import-done');
        this.dialog = false;
      } catch (e) {
        this.showNotify({ type: 'error', message: 'Что-то пошло не так. Обратитесь к администратору' });
      } finally {
        this.isLoading = false;
      }
    },

    fillPropertiesFields() {
      this.propertyKeys.forEach((item) => {
        this.$set(this.form.propertiesFields, item.id, item.name);
      });
    },

    cleanData() {
      this.form = createDefaultForm();
      this.selectedFile = null;
      this.importData = null;
      this.layerStepType = null;
      this.newLayerData = null;
      this.importableSummary = createDefaultImportableSummary();
    },
  },
};
</script>

<style lang="scss" scoped>
.data-import-dialog {
  &__text-success {
    color: #28a745;
  }
  &__text-danger {
    color: #dc3545;
  }
}
</style>
